import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { CITIES } from "../../lib/site";
import { track } from "../../lib/analytics";
import { getAttribution } from "../../lib/attribution";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREA_OPTIONS = [
  "Garage",
  "Driveway",
  "Patio / Outdoor Space",
  "Interior Concrete",
  "Commercial Space",
  "Other / Not Sure",
];

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const EMPTY = {
  name: "", email: "", phone: "", address: "", city: "Prescott",
  service_type: "Garage", square_footage: "", preferred_date: "", details: "",
};

const inputCls =
  "w-full h-14 px-4 bg-zinc-900 border border-zinc-800 text-white focus:outline-none focus:border-[#C69453] transition-colors text-lg";
const labelCls = "text-sm font-semibold uppercase tracking-wider text-zinc-300 block mb-2";

const LeadForm = ({ formName = "Free Estimate Request", thankYouPath = "/thank-you" }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        square_footage: form.square_footage ? parseInt(form.square_footage, 10) : null,
        page_url: window.location.href,
        form_name: formName,
        submitted_at: new Date().toISOString(),
        ...getAttribution(),
      });
      track("generate_lead", { service: form.service_type, city: form.city, value: 0, currency: "USD" });
      // Fire Meta Pixel Lead here only for non-Meta forms. The Meta LP counts its
      // Lead on its dedicated /lp/meta/thank-you page so it stays separate from Google.
      if (formName !== "meta_lp" && typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: formName, content_category: form.service_type });
      }
      navigate(thankYouPath, { state: { fromForm: true, name: form.name, service: form.service_type, city: form.city } });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="py-20 md:py-24 bg-[#0A0A0A] border-t border-zinc-900 scroll-mt-24">
      <div className="max-w-4xl mx-auto px-5 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-[#C69453] text-sm font-semibold tracking-[0.2em] uppercase">No Obligation • Free Estimate</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mt-2">Request Your Free Estimate</h2>
          <p className="text-zinc-400 text-base md:text-lg mt-3">
            Fill out this quick form and Tyler will personally review your project and schedule a friendly on-site visit.
          </p>
        </div>

        <form onSubmit={onSubmit} className="bg-zinc-950 border border-zinc-800 p-6 md:p-12 space-y-6 shadow-2xl relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#C69453]" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>Full Name *</label>
              <input name="name" value={form.name} onChange={onChange} required placeholder="John Doe" data-testid={HOME.leadFormName} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone Number *</label>
              <input type="tel" name="phone" value={form.phone} onChange={onChange} required placeholder="(928) 555-0199" data-testid={HOME.leadFormPhone} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>Email Address *</label>
              <input type="email" name="email" value={form.email} onChange={onChange} required placeholder="john@example.com" data-testid={HOME.leadFormEmail} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Property Address</label>
              <input name="address" value={form.address} onChange={onChange} placeholder="123 Granite Mountain Rd" data-testid={HOME.leadFormAddress} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>City *</label>
              <select name="city" value={form.city} onChange={onChange} data-testid={HOME.leadFormCity} className={`${inputCls} cursor-pointer`}>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>What area are you looking to transform? *</label>
              <select name="service_type" value={form.service_type} onChange={onChange} data-testid={HOME.leadFormService} className={`${inputCls} cursor-pointer`}>
                {AREA_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>Approx. Sq. Footage</label>
              <input type="number" name="square_footage" value={form.square_footage} onChange={onChange} placeholder="e.g. 400" data-testid={HOME.leadFormSqft} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Preferred Date</label>
              <input type="date" name="preferred_date" value={form.preferred_date} onChange={onChange} min={new Date().toISOString().split("T")[0]} data-testid="lead-form-preferred-date" className={`${inputCls} cursor-pointer`} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Project Details</label>
            <input name="details" value={form.details} onChange={onChange} placeholder="Cracks, timing, color preference..." data-testid={HOME.leadFormDetails} className={inputCls} />
          </div>

          <button type="submit" disabled={submitting} data-testid={HOME.leadFormSubmit} className="w-full bg-[#C69453] hover:bg-[#AE7F44] text-black font-extrabold text-xl py-5 transition-colors flex items-center justify-center space-x-3 disabled:opacity-50">
            {submitting ? (<><Loader2 className="w-6 h-6 animate-spin" /><span>Sending...</span></>) : (<><span>Submit My Free Estimate Request</span><ArrowRight className="w-6 h-6" /></>)}
          </button>

          <p className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
            <ShieldCheck className="w-4 h-4 text-[#C69453]" />
            Your information stays private. No spam, no pressure — just an honest quote.
          </p>
        </form>
      </div>
    </section>
  );
};

export default LeadForm;
